import { Inbox, RotateCw } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";
import useTranslations from "@/shared/lang/useTranslations";

import { CryptoSellStatusPill } from "../components/sale/CryptoSellStatusPill";
import { DepositSlip } from "../components/sale/DepositSlip";
import { SaleSummaryCard } from "../components/sale/SaleDetailsList";
import { SaleOutcome } from "../components/sale/SaleOutcome";
import { SaleTimeline } from "../components/sale/SaleTimeline";
import { MonoValue } from "../components/shared/CryptoSwapBits";
import { useAmountFormatter } from "../hooks/useAmountFormatter";
import { useCryptoSellOrder } from "../hooks/useCryptoSellData";
import type { OtcSellOrder } from "../types/cryptoSell.types";
import { isLiveSale, showsDepositInstructions } from "../utils/cryptoSellStatus";
import { CRYPTO_SELL_HISTORY_PATH, CRYPTO_SELL_PATH } from "../utils/cryptoSwapLinks";

const HEADLINE_KEYS: Record<string, keyof CryptoSwapsTranslations> = {
  AWAITING_DEPOSIT: "sell_headline_awaiting",
  PROCESSING: "sell_headline_processing",
  COP_SENT: "sell_headline_cop_sent",
  COMPLETED: "sell_headline_completed",
  EXPIRED: "sell_headline_expired",
  REFUNDING: "sell_headline_refunding",
  REFUNDED: "sell_headline_refunded",
};

/** `/app/crypto-swaps/sales/:saleId` — one sale, polled from the deposit to the pesos in the wallet. */
export default function CryptoSellOrderPage() {
  const { saleId = null } = useParams<{ saleId: string }>();
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter();
  const { order: sale, isLoading, isStale, notFound, lastUpdatedAt, refresh } = useCryptoSellOrder(saleId);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: t("page_breadcrumb_wallets"), to: "/app/wallets" },
        { label: t("sell_page_title"), to: CRYPTO_SELL_PATH },
        { label: t("history_hub_title"), to: CRYPTO_SELL_HISTORY_PATH },
        { label: t("sell_order_breadcrumb") },
      ],
      [t],
    ),
  );

  // The submit button was unmounted by the navigation; move focus once per sale, never on a poll.
  const focusedSaleIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sale || focusedSaleIdRef.current === sale.id) return;
    focusedSaleIdRef.current = sale.id;
    headingRef.current?.focus();
  }, [sale]);

  if (isLoading && !sale) {
    return (
      <div className="flex flex-col gap-6">
        <ColocaSkeleton className="h-28 rounded-2xl" />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <ColocaSkeleton className="h-[480px] rounded-3xl" />
          <ColocaSkeleton className="h-72 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (notFound || !sale) {
    return (
      <ColocaEmpty
        icon={Inbox}
        title={t("sell_order_not_found_title")}
        description={t("order_not_found_description")}
      >
        <ColocaButton variant="primary" render={<Link to={CRYPTO_SELL_HISTORY_PATH} />}>
          {t("order_back_to_history")}
        </ColocaButton>
      </ColocaEmpty>
    );
  }

  const live = isLiveSale(sale.status);

  return (
    <div className="flex flex-col gap-6 pb-16">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex min-w-0 flex-col gap-3">
          <CryptoSellStatusPill status={sale.status} />
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="rounded-sm text-3xl leading-tight font-bold tracking-tight text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring md:text-4xl"
          >
            {t(HEADLINE_KEYS[sale.status] ?? "sell_headline_processing", headlineArgs(sale, format))}
          </h1>
          <p className="max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
            {t("sell_subtitle", { key: sale.payout_key })}
          </p>
        </div>

        {live && (
          <div className="flex shrink-0 items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3">
            <span className="relative flex size-2.5">
              <span
                aria-hidden="true"
                className="absolute -inset-1 animate-pulse rounded-full bg-success/30"
              />
              <span aria-hidden="true" className="absolute inset-0 rounded-full bg-success" />
            </span>
            <span className="flex flex-col">
              <span className="text-[13px] font-semibold">{t("order_live_title")}</span>
              <MonoValue className="text-xs text-muted-foreground">
                {lastUpdatedAt
                  ? t("order_live_updated", { time: lastUpdatedAt.toLocaleTimeString(activeLanguage) })
                  : "—"}
              </MonoValue>
            </span>
          </div>
        )}
      </header>

      {isStale && (
        <ColocaAlert
          variant="secondary"
          text={t("order_live_stale")}
          actions={
            <ColocaButton variant="default" icon={RotateCw} onClick={refresh}>
              {t("history_refresh")}
            </ColocaButton>
          }
        />
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-4">
          {showsDepositInstructions(sale) ? <DepositSlip sale={sale} /> : <SaleOutcome sale={sale} />}
          <SaleTimeline sale={sale} />
        </div>
        <SaleSummaryCard sale={sale} />
      </div>
    </div>
  );
}

function headlineArgs(
  sale: OtcSellOrder,
  format: (value: string, currency: string) => string,
): Record<string, string> {
  const deposit = `${format(sale.deposit.amount, sale.deposit.currency)} ${sale.deposit.currency}`;
  const credited = sale.credit.amount ?? sale.proceeds.paid_amount ?? sale.proceeds.amount;
  return {
    amount:
      sale.status === "COMPLETED"
        ? `${format(credited, sale.credit.currency)} ${sale.credit.currency}`
        : deposit,
    asset: sale.deposit.currency,
  };
}
